import { useEffect, useState } from "react";
import AzioniHomeCard from "@/components/AzioniHomeCard";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { euro, fmtTime, fmtDate, pctIt, ptIt } from "@/lib/api";
import { toast } from "sonner";
import { NoteView } from "@/components/LongText";
import { useAuth } from "@/context/AuthContext";
import MiaGiornata from "@/pages/MiaGiornata";
import HomeTeam from "@/pages/HomeTeam";
import { PareggioContatore } from "@/components/PareggioContatore";
import CentraleOperativa from "@/pages/CentraleOperativa";
import CabinaRegia from "@/pages/CabinaRegia";
import DaSeguireTeam from "@/components/DaSeguireTeam";
import TaskCrm from "@/components/TaskCrm";
import ComunicazioniClienti from "@/components/ComunicazioniClienti";
import { KpiCard, SectionTitle } from "@/components/common";
import { Building2, Footprints, FileText, Wallet, Phone, MessageSquareWarning, UserCog, Clock, CheckCircle2, CalendarClock, AlertTriangle, Rocket, FileSignature, FileWarning, IdCard, ChevronRight, Crown } from "lucide-react";
import ChiamateReminder from "@/components/ChiamateReminder";
import { EsitoModal, apptNeedsEsito } from "@/components/EsitoModal";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { PROVENIENZE } from "@/pages/Notizie";
import { ObiettiviContatori } from "@/components/ObiettiviContatori";
import FotografoServizi from "@/pages/FotografoServizi";
import { FattureDaSaldare } from "@/pages/LeMieFatture";
import { AzioniRiunioniCard } from "@/components/AzioniRiunioniCard";
import { scadutoLabel } from "@/pages/Task";


const PROV_COLORS = ["#FC82ED", "#38BDF8", "#FFB020", "#34D399", "#A78BFA", "#F87171", "#FB923C", "#22D3EE"];

const localISO = (dt) => { const t = new Date(dt); t.setMinutes(t.getMinutes() - t.getTimezoneOffset()); return t.toISOString().slice(0, 10); };

export default function Oggi() {
  const { user, isDirezione, isSegreteria, isAgente, isDirettore, isFotografo, vistaMia } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  // La Direzione (management) non vede/gestisce le attività operative degli agenti.
  const showOperational = !(isDirezione && !isSegreteria);
  const pipeLabel = user?.role === "direzione" ? "Pipeline Aziendale" : user?.role === "direttore_commerciale" ? "Pipeline Team" : "Pipeline Personale";
  const [d, setD] = useState(null);
  const [attivita, setAttivita] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [pipeFilter, setPipeFilter] = useState("tutte");
  const [provTrend, setProvTrend] = useState([]);
  const [ciDocs, setCiDocs] = useState({ in_scadenza: [], scaduti: [] });
  const [contest, setContest] = useState(null);
  const [esitoAppt, setEsitoAppt] = useState(null);
  const [vistaOperativa, setVistaOperativa] = useState(false);

  const load = () => {
    if (isFotografo) return;
    api.get("/dashboard/oggi").then((r) => setD(r.data));
    api.get("/attivita").then((r) => setAttivita(r.data)).catch(() => {});
    api.get("/pipeline").then((r) => setPipeline(r.data)).catch(() => {});
    api.get("/dashboard/provenienza-storico").then((r) => setProvTrend(r.data)).catch(() => {});
    api.get("/dashboard/documenti-identita-scadenza?days=90").then((r) => setCiDocs(r.data)).catch(() => {});
    api.get("/contest/mensile").then((r) => setContest(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const onFocus = () => load();
    const onVisible = () => { if (!document.hidden) load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  useEffect(() => {
    if (d && sp.get("focus") === "ci") setTimeout(() => document.querySelector('[data-testid="ci-scadenza-panel"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  }, [d, sp]);

  const toggleTask = async (t) => {
    const nuovo = t.stato === "completato" ? "aperto" : "completato";
    setAttivita((x) => x.map((i) => (i.id === t.id ? { ...i, stato: nuovo } : i)));
    try { await api.put(`/attivita/${t.id}`, { stato: nuovo }); } catch { load(); }
  };

  const conPareggio = (el) => <><PareggioContatore />{el}</>;
  if (isFotografo) return <FotografoServizi />;
  if (isSegreteria) return <><AzioniHomeCard /><AzioniRiunioniCard /><CentraleOperativa /></>;
  if (isAgente || vistaMia) return <><AzioniHomeCard /><ObiettiviContatori /><TaskCrm />{conPareggio(<MiaGiornata />)}</>;
  if (isDirettore) return <><AzioniHomeCard /><DaSeguireTeam />{conPareggio(<HomeTeam />)}</>;
  if (isDirezione && !vistaOperativa) return <><AzioniHomeCard /><AzioniRiunioniCard /><FattureDaSaldare /><DaSeguireTeam />{conPareggio(<CabinaRegia onVistaOperativa={() => setVistaOperativa(true)} />)}</>;
  if (!d) return <Skeleton />;

  const today = new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/Rome" });
  const todayISO = localISO(new Date());
  const taskOggi = [
    ...attivita.filter((a) => a.stato !== "completato" && a.scadenza && a.scadenza < todayISO).sort((a, b) => a.scadenza.localeCompare(b.scadenza)),
    ...attivita.filter((a) => (a.scadenza || todayISO) === todayISO),
  ];
  const OPEN_PROP = ["presentata", "in_valutazione", "controproposta", "da_firmare", "bozza"];
  const pipeMatch = (deal) => pipeFilter === "tutte"
    || (pipeFilter === "proposte" && deal.proposta && OPEN_PROP.includes(deal.proposta.stato))
    || (pipeFilter === "preliminari" && deal.preliminare)
    || (pipeFilter === "rogiti" && deal.rogito && deal.rogito.stato === "programmato");
  const filteredPipeline = pipeline.filter(pipeMatch);

  return (
    <div className="fade-up">
      <AzioniHomeCard />
      {isDirezione && (
        <button data-testid="btn-torna-cabina" onClick={() => setVistaOperativa(false)}
          className="mb-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-semibold hover:bg-slate-100">
          <ChevronRight size={15} className="rotate-180" /> Torna alla Cabina di regia
        </button>
      )}
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-[var(--muted)] mb-1">{today}</div>
        <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight">Ciao, {user?.name?.split(" ")[0]} 👋</h1>
      </div>

      {contest?.vincitore?.agent_id === user?.id && (
        <div data-testid="contest-winner-badge" className="card-immo p-5 mb-8 border-amber-300/60 bg-gradient-to-r from-amber-50 to-transparent flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-amber-400/20 border border-amber-300 flex items-center justify-center shrink-0"><Crown className="text-amber-500" size={20} /></div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-sm uppercase tracking-wide text-amber-600">🏆 Sei il Vincitore del mese!</div>
            <p className="text-sm text-slate-600 mt-1">Sei in testa al contest con <b className="text-amber-600">{ptIt(contest.vincitore.punti_totali)} punti</b> questo mese. Continua così!</p>
          </div>
        </div>
      )}

      {showOperational && !isSegreteria && <ChiamateReminder />}

      {d.documenti_urgenti?.totale > 0 && (() => {
        const u = d.documenti_urgenti;
        const parts = [
          u.ape > 0 && `${u.ape} APE`,
          u.visura > 0 && `${u.visura} ${u.visura === 1 ? "visura" : "visure"}`,
          u.agibilita > 0 && `${u.agibilita} ${u.agibilita === 1 ? "agibilità" : "agibilità"}`,
        ].filter(Boolean);
        return (
          <div data-testid="doc-urgente-alert" onClick={() => nav("/documenti-scadenza?days=7")}
            className="card-immo p-4 mb-8 border-red-300 bg-red-50 flex items-center gap-3 cursor-pointer hover:-translate-y-0.5 transition-transform">
            <AlertTriangle className="text-red-500 shrink-0" size={20} />
            <div className="text-sm text-red-700">
              <b>{u.totale}</b> {u.totale === 1 ? "documento in scadenza" : "documenti in scadenza"} entro 5 giorni: {parts.join(" · ")}. Rinnovali prima che scadano.
            </div>
          </div>
        );
      })()}

      <CiScadenzaPanel data={ciDocs} nav={nav} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <KpiCard testid="kpi-incarichi-acquisiti" label="Incarichi Acquisiti" value={d.incarichi_acquisiti} icon={Building2} accent onClick={() => nav("/immobili?filter=acquisiti")}
          sub={d.incarichi_mese ? <span data-testid="incarichi-trend" className={d.incarichi_mese.trend === "up" ? "text-emerald-600 font-semibold" : d.incarichi_mese.trend === "down" ? "text-red-500 font-semibold" : "text-slate-500 font-semibold"}>{d.incarichi_mese.trend === "up" ? "▲" : d.incarichi_mese.trend === "down" ? "▼" : "■"} {d.incarichi_mese.delta >= 0 ? "+" : ""}{d.incarichi_mese.delta} ({d.incarichi_mese.delta_pct >= 0 ? "+" : ""}{pctIt(d.incarichi_mese.delta_pct)}) vs mese scorso ({d.incarichi_mese.corrente} vs {d.incarichi_mese.precedente})</span> : null} />
        <KpiCard testid="kpi-doc-in-corso" label="Documentazione in Corso" value={d.documentazione_in_corso} icon={FileText} onClick={() => nav("/documentazione-in-corso")} />
        <KpiCard testid="kpi-pronti-pubblicazione" label="Pronti per Pubblicazione" value={d.pronti_pubblicazione} icon={Rocket} onClick={() => nav("/pronti-pubblicazione")} />
        <KpiCard testid="kpi-immobili-attivi" label="Immobili Attivi" value={d.immobili_attivi} icon={CheckCircle2} accent onClick={() => nav("/immobili?stato=attivo")} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard testid="kpi-appuntamenti" label="Appuntamenti Oggi" value={d.appuntamenti_oggi_count} icon={CalendarClock} accent onClick={() => nav("/appuntamenti-oggi")} />
        <KpiCard testid="kpi-proposte" label="Proposte Aperte" value={d.proposte_aperte} icon={FileText} onClick={() => nav("/proposte")} />
        <KpiCard testid="kpi-preliminari" label="Preliminari" value={d.preliminari ?? 0} icon={FileSignature} onClick={() => nav("/preliminari")} />
        <KpiCard testid="kpi-pipeline" label={pipeLabel} value={euro(d.pipeline_personale)} icon={Wallet} accent />
      </div>

      {d.provenienza_stats && Object.values(d.provenienza_stats).some((v) => v > 0) && (
        <div className="card-immo p-5 mb-8" data-testid="provenienza-chart">
          <SectionTitle sub={isDirezione && !isSegreteria ? "Da dove arrivano le notizie di acquisizione dell'agenzia." : "Da dove arrivano le tue notizie di acquisizione."}>Notizie per provenienza</SectionTitle>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={Object.entries(d.provenienza_stats).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }))}
                   dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {Object.entries(d.provenienza_stats).filter(([, v]) => v > 0).map((e, i) => <Cell key={i} fill={PROV_COLORS[i % PROV_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #ECECEF", borderRadius: 12 }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {provTrend.some((m) => PROVENIENZE.reduce((s, p) => s + (m[p] || 0), 0) > 0) && (
        <div className="card-immo p-5 mb-8" data-testid="provenienza-trend">
          <SectionTitle sub="Andamento delle notizie per provenienza negli ultimi 6 mesi.">Trend provenienza</SectionTitle>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={provTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ECECEF" vertical={false} />
              <XAxis dataKey="mese" stroke="#6B7280" fontSize={11} />
              <YAxis stroke="#6B7280" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #ECECEF", borderRadius: 12 }} />
              <Legend />
              {PROVENIENZE.map((p, i) => <Bar key={p} dataKey={p} fill={PROV_COLORS[i % PROV_COLORS.length]} radius={[4, 4, 0, 0]} />)}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {pipeline.length > 0 && (
        <div className="mb-8" data-testid="pipeline-section">
          <SectionTitle sub="Dalla proposta al rogito: lo stato di ogni trattativa. Tocca una riga per aprirla.">Pipeline trattative</SectionTitle>
          <div className="flex gap-2 mb-3 flex-wrap">
            {[["tutte", "Tutte"], ["proposte", "Proposte aperte"], ["preliminari", "Preliminari"], ["rogiti", "Rogiti programmati"]].map(([v, l]) => (
              <button key={v} data-testid={`pipe-filter-${v}`} onClick={() => setPipeFilter(v)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${pipeFilter === v ? "bg-[#FC82ED] text-black border-[#FC82ED]" : "bg-white text-[var(--muted)] border-[var(--border)] hover:text-[#111111]"}`}>{l}</button>
            ))}
          </div>
          <div className="space-y-2.5">
            {filteredPipeline.length === 0 && <div className="card-immo p-6 text-center text-[var(--muted)] text-sm" data-testid="pipeline-empty">Nessuna trattativa in questa fase.</div>}
            {filteredPipeline.map((deal, i) => (
              <div key={i} data-testid={`pipeline-deal-${i}`}
                role="button" tabIndex={0}
                onClick={() => deal.immobile_id ? nav(`/immobili/${deal.immobile_id}`) : nav("/preliminari")}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); deal.immobile_id ? nav(`/immobili/${deal.immobile_id}`) : nav("/preliminari"); } }}
                className="card-immo p-4 flex flex-col md:flex-row md:items-center gap-3 cursor-pointer hover:-translate-y-0.5 transition-transform focus:outline-none focus:ring-2 focus:ring-[#FC82ED]/40">
                <div className="min-w-0 md:w-64 shrink-0">
                  <div className="font-semibold text-sm truncate flex items-center gap-2"><Building2 size={14} className="text-[#FC82ED] shrink-0" /> {deal.immobile_titolo || "Immobile"}</div>
                  <div className="text-xs text-[var(--muted)] truncate">{deal.acquirente || "—"}{deal.importo ? ` · ${euro(deal.importo)}` : ""}</div>
                </div>
                <div className="flex items-center gap-2 flex-1 flex-wrap">
                  <PipeStage label="Proposta" stage={deal.proposta} />
                  <span className="text-[var(--muted)] text-xs">→</span>
                  <PipeStage label="Preliminare" stage={deal.preliminare} />
                  <span className="text-[var(--muted)] text-xs">→</span>
                  <PipeStage label="Rogito" stage={deal.rogito} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Task del giorno */}
        <div className="lg:col-span-2">
          <div className="flex items-start justify-between gap-3 mb-1">
            <SectionTitle sub="I tuoi task di oggi e quelli scaduti da completare o rimandare, sincronizzati con la pagina Task.">Task del giorno</SectionTitle>
            <button data-testid="home-vedi-tutti-task" onClick={() => nav("/task")} className="text-sm font-semibold text-[#FC82ED] hover:underline shrink-0 mt-1">Vedi tutti i task →</button>
          </div>
          <div className="space-y-2.5" data-testid="home-task-list">
            {taskOggi.length === 0 && <div className="card-immo p-6 text-center text-[var(--muted)] text-sm" data-testid="home-task-empty">Nessun task aperto per oggi</div>}
            {taskOggi.map((t) => {
              const done = t.stato === "completato";
              const prioCls = t.priorita === "alta" ? "text-red-500 bg-red-400/10 border-red-400/30" : t.priorita === "bassa" ? "text-slate-500 bg-slate-100 border-slate-300" : "text-[#FC82ED] bg-[#FC82ED]/10 border-[#FC82ED]/30";
              return (
                <div key={t.id} data-testid={`home-task-${t.id}`} className={`card-immo p-4 flex items-center gap-4 transition-all ${done ? "opacity-70" : ""}`}>
                  <button data-testid={`home-task-checkbox-${t.id}`} onClick={() => toggleTask(t)} aria-label="Completa"
                          className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all ${done ? "bg-[#FC82ED] text-black" : "border-2 border-[var(--border)] hover:border-[#FC82ED] text-transparent hover:text-[#FC82ED]"}`}>
                    <CheckCircle2 size={14} className={done ? "" : "opacity-0"} />
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span data-testid={`home-task-title-${t.id}`} className={`font-semibold text-sm ${done ? "line-through text-[var(--muted)]" : ""}`}>{t.titolo}</span>
                      {done && <span data-testid={`home-task-status-${t.id}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border text-emerald-600 bg-emerald-400/10 border-emerald-400/30">Completato</span>}
                      {!done && t.scadenza && t.scadenza < todayISO && <span data-testid={`home-task-scaduto-${t.id}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border text-red-500 bg-red-400/10 border-red-400/30">{scadutoLabel(t.scadenza)}</span>}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border ${prioCls}`}>{t.priorita || "media"}</span>
                    </div>
                    {t.descrizione && <NoteView text={t.descrizione} className={`text-xs mt-0.5 ${done ? "line-through text-[var(--muted)]" : "text-[var(--muted)]"}`} />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick lists */}
        <div>
          <SectionTitle>In Evidenza</SectionTitle>
          <div className="space-y-3">
            <QuickRow icon={Phone} label="Lead da richiamare" value={d.lead_da_richiamare} onClick={() => nav("/notizie")} testid="quick-lead" />
            {showOperational && <QuickRow icon={AlertTriangle} label="Esiti mancanti" value={d.esiti_mancanti ?? 0} testid="quick-esiti-mancanti" danger />}
            <QuickRow icon={MessageSquareWarning} label="Feedback visita mancanti" value={d.feedback_mancanti} onClick={() => nav("/visite")} testid="quick-feedback" accent />
            <QuickRow icon={FileText} label="Proposte aperte" value={d.proposte_aperte} onClick={() => nav("/proposte")} testid="quick-proposte" />
            <QuickRow icon={FileWarning} label="Documenti in scadenza" value={d.documenti_in_scadenza ?? 0} onClick={() => nav("/documenti-scadenza")} testid="quick-doc-scadenza" accent />
            <QuickRow icon={AlertTriangle} label="Task scaduti" value={d.tasks_scaduti} testid="quick-scaduti" danger />
          </div>

          <SectionTitle>Appuntamenti di Oggi</SectionTitle>
          <div className="space-y-2">
            {d.appuntamenti_oggi.length === 0 && <div className="text-sm text-[var(--muted)] card-immo p-4">Nessun appuntamento oggi.</div>}
            {d.appuntamenti_oggi.map((a) => {
              const esitabile = apptNeedsEsito(a);
              return (
              <div key={a.id} className="card-immo p-3 flex items-center gap-3">
                <div className="mono text-sm font-bold text-[#FC82ED] w-12 shrink-0">{fmtTime(a.data)}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{a.titolo}</div>
                  <div className="text-xs text-[var(--muted)]">{(a.tipo || "").replace(/_/g, " ")} · {a.stato}{a.esito_registrato ? " · esito ✓" : ""}</div>
                </div>
                {esitabile && <button data-testid={`esito-btn-${a.id}`} onClick={() => setEsitoAppt(a)} className="shrink-0 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-[#FC82ED]/15 text-[#111111] hover:bg-[#FC82ED]/25">Com'è andata?</button>}
              </div>
              );
            })}
          </div>
        </div>
      </div>
      {isDirezione && <div className="mt-8"><ComunicazioniClienti /></div>}
      {esitoAppt && <EsitoModal appt={esitoAppt} onClose={() => setEsitoAppt(null)} onSaved={() => { setEsitoAppt(null); load(); }} />}
    </div>
  );
}

function CiScadenzaPanel({ data, nav }) {
  const [days, setDays] = useState(60);
  const scaduti = data?.scaduti || [];
  const allInScadenza = data?.in_scadenza || [];
  const daysLeft = (iso) => { try { return Math.ceil((new Date(iso) - new Date()) / 86400000); } catch { return null; } };
  const inScadenza = allInScadenza
    .filter((r) => { const dl = daysLeft(r.scadenza); return dl == null || dl <= days; })
    .sort((a, b) => (a.scadenza || "").localeCompare(b.scadenza || ""));
  if (scaduti.length === 0 && allInScadenza.length === 0) return null;

  const goTo = (r) => nav(`/${r.route}?open=${r.cliente_id}&tab=docs`);

  const urgency = (dl) => {
    if (dl == null) return null;
    if (dl <= 7) return { label: "Urgente", cls: "text-red-600 bg-red-100 border-red-300" };
    if (dl <= 15) return { label: "A breve", cls: "text-orange-600 bg-orange-100 border-orange-300" };
    if (dl <= 30) return { label: "Da pianificare", cls: "text-[#9A6A00] bg-[#FFB020]/15 border-[#FFB020]/40" };
    return { label: "In programma", cls: "text-slate-500 bg-slate-100 border-slate-300" };
  };

  const Row = (r, expired) => {
    const dl = daysLeft(r.scadenza);
    const urg = urgency(dl);
    return (
      <button key={r.id} data-testid={`ci-scad-row-${r.id}`} onClick={() => goTo(r)}
        className="w-full card-immo p-3.5 flex items-center gap-3 text-left hover:-translate-y-0.5 transition-transform">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${expired ? "bg-red-400/10 text-red-500" : "bg-[#FFB020]/10 text-[#FFB020]"}`}>
          <IdCard size={17} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">{r.cliente_nome}</div>
          <div className="text-[11px] text-[var(--muted)] flex items-center gap-1.5 flex-wrap">
            <span className="uppercase tracking-wide">{r.cliente_tipo === "venditore" ? "Venditore" : "Acquirente"}</span>
            <span>·</span>
            <span>Documento d'identità</span>
            {!expired && urg && <span data-testid={`ci-urgency-${r.id}`} className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase border ${urg.cls}`}>{urg.label}</span>}
          </div>
        </div>
        <div className={`text-right shrink-0 ${expired ? "text-red-500" : "text-[#FFB020]"}`}>
          <div className="text-sm font-semibold">{fmtDate(r.scadenza)}</div>
          <div className="text-[11px]">{expired ? (dl != null ? `Scaduto da ${Math.abs(dl)}gg` : "Scaduto") : (dl != null ? `tra ${dl}gg` : "")}</div>
        </div>
        <ChevronRight size={16} className="text-[var(--muted)] shrink-0" />
      </button>
    );
  };

  return (
    <div className="card-immo p-5 mb-8" data-testid="ci-scadenza-panel">
      <div className="flex items-center gap-2 mb-1">
        <IdCard className="text-[#FC82ED]" size={18} />
        <SectionTitle sub="Carte d'identità dei clienti da rinnovare. Tocca una riga per aprire la scheda cliente nella sezione Documenti.">Documenti d'identità in scadenza</SectionTitle>
      </div>
      <div className="flex items-center gap-2 mt-2" data-testid="ci-days-filter">
        <span className="text-[11px] text-[var(--muted)]">In scadenza entro:</span>
        {[30, 60, 90].map((n) => (
          <button key={n} data-testid={`ci-days-${n}`} onClick={() => setDays(n)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${days === n ? "bg-[#FC82ED] text-black border-[#FC82ED]" : "bg-white text-slate-600 border-[var(--border)] hover:border-[#FC82ED]/50"}`}>{n}gg</button>
        ))}
      </div>

      {scaduti.length > 0 && (
        <div className="mt-3" data-testid="ci-scaduti-group">
          <div className="text-[11px] font-bold uppercase tracking-wider text-red-500 mb-2 flex items-center gap-1.5"><AlertTriangle size={13} /> Scaduti ({scaduti.length})</div>
          <div className="space-y-2">{scaduti.map((r) => Row(r, true))}</div>
        </div>
      )}

      <div className="mt-4" data-testid="ci-in-scadenza-group">
        <div className="text-[11px] font-bold uppercase tracking-wider text-[#FFB020] mb-2 flex items-center gap-1.5"><Clock size={13} /> In scadenza · entro {days} giorni ({inScadenza.length})</div>
        {inScadenza.length > 0
          ? <div className="space-y-2">{inScadenza.map((r) => Row(r, false))}</div>
          : <div className="text-[12px] text-[var(--muted)] py-1" data-testid="ci-in-scadenza-empty">Nessun documento in scadenza entro {days} giorni.</div>}
      </div>
    </div>
  );
}

function PipeStage({ label, stage }) {
  const active = !!stage;
  const st = stage?.stato;
  const cls = !active ? "bg-slate-100 text-slate-400 border-slate-200"
    : st === "annullato" ? "bg-slate-100 text-slate-400 border-slate-300 line-through"
    : (st === "completato" || st === "rogitato" || st === "accettata" || st === "firmato") ? "bg-emerald-400/10 text-emerald-600 border-emerald-400/30"
    : "bg-[#FC82ED]/10 text-[#FC82ED] border-[#FC82ED]/30";
  return (
    <div className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${cls}`}>
      <span className="uppercase tracking-wide text-[10px] block opacity-70">{label}</span>
      {active ? (st || "").replace(/_/g, " ") : "—"}
    </div>
  );
}

function QuickRow({ icon: Icon, label, value, onClick, testid, accent, danger }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={!onClick}
            className="card-immo w-full p-4 flex items-center gap-3 text-left hover:-translate-y-0.5 transition-transform disabled:hover:translate-y-0">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${danger ? "bg-red-400/10 text-red-400" : accent ? "bg-[#FC82ED]/15 text-[#FC82ED]" : "bg-slate-100 text-slate-600"}`}>
        <Icon size={17} />
      </div>
      <div className="flex-1 text-sm font-medium">{label}</div>
      <div className={`mono font-bold text-lg ${danger && value > 0 ? "text-red-400" : "text-[#111111]"}`}>{value}</div>
    </button>
  );
}

function Skeleton() {
  return (
    <div className="space-y-4">
      <div className="skeleton h-10 w-64 rounded-xl" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="skeleton h-28 rounded-2xl" />)}</div>
      <div className="skeleton h-96 rounded-2xl" />
    </div>
  );
}
